<script lang="ts">
	import type { getPodcastEpisodePage } from '#lib/websites/talks/api/getPodcastEpisodePage.js';
	import { TalksRoutes } from '#lib/websites/talks/constants/routes.js';
	import { TALK_YOUTUBE_CHANNEL } from '#lib/websites/talks/constants/socials.js';
	import { page, navigating } from '$app/state';
	import { ShareIcon } from '$global/assets/icons/ShareIcon';
	import { YouTubeIcon } from '$global/assets/icons/YouTubeIcon';
	import { Button } from '$global/components/Button';
	import { createDialogStores } from '$global/components/Dialog';
	import {
		Lightbox,
		type GalleryItem,
		GalleryMedia,
	} from '$global/components/Lightbox';
	import { Link } from '$global/components/Link';
	import { LinkButton } from '$global/components/LinkButton';
	import {
		Markdown,
		type MarkdownTokenList,
	} from '$global/components/Markdown';
	import { ShareDialog } from '$global/components/Share/ShareDialog';
	import { Tabs } from '$global/components/Tabs';
	import { Txt } from '$global/components/Txt';
	import { VideoPlayer } from '$global/components/VideoPlayer';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import type { TalksPDPPageData } from '$routes/talks/[slug]/types';

	interface Props {
		type: 'podcast' | 'talk';
		dataCy: string;
		images: NonNullable<
			Awaited<ReturnType<typeof getPodcastEpisodePage>>['podcastEpisode']
		>['reference_images'];
		detailsLabel: string;
		// Parsed by the page's server load: null when there is nothing to show.
		filmDetails: MarkdownTokenList | undefined | null;
		transcript: MarkdownTokenList | undefined | null;
		furtherMedia: MarkdownTokenList | undefined | null;
		credits: MarkdownTokenList | undefined | null;
		title: string;
		children?: import('svelte').Snippet;
		youtubeUrl: string | undefined | null;
		timePeriod: string | undefined | null;
		shareUrl: string;
		speakers: {
			name: string;
			href: string;
		}[];
	}

	let {
		type,
		children,
		dataCy,
		images,
		detailsLabel,
		filmDetails,
		transcript,
		furtherMedia,
		credits,
		title,
		youtubeUrl,
		shareUrl,
		timePeriod,
		speakers,
	}: Props = $props();

	let activeTab = $state(0);

	let dataCyPrefix = $derived(`${dataCy}-details`);
	const shareDialogStores = createDialogStores();

	const {
		states: { open },
	} = shareDialogStores;

	const handleOpenShareDialog = () => open.set(true);

	let tags = $derived(page.data.tags as TalksPDPPageData['tags']);
	let tabs = $derived([
		...(filmDetails
			? [
					{
						id: '1',
						title: detailsLabel,
						dataCy: `${dataCyPrefix}-film-details`,
						markdown: filmDetails,
					},
				]
			: []),
		...(images?.length
			? [
					{
						id: '5',
						title: 'Images',
						dataCy: `${dataCyPrefix}-images`,
						markdown: undefined,
					},
				]
			: []),
		...(transcript
			? [
					{
						id: '2',
						title: 'Transcript',
						dataCy: `${dataCyPrefix}-transcript`,
						markdown: transcript,
					},
				]
			: []),
		...(furtherMedia
			? [
					{
						id: '3',
						title: 'Further Media',
						dataCy: `${dataCyPrefix}-further-media`,
						markdown: furtherMedia,
					},
				]
			: []),
		...(credits
			? [
					{
						id: '4',
						title: 'Credits',
						dataCy: `${dataCyPrefix}-credits`,
						markdown: credits,
					},
				]
			: []),
	]);

	let shareIntro = $derived(`HENI Talks: ${title}`);
	let isLightboxOpen = $state(false);
	let selectedItemIndex = $state(0);

	let selectedItem: GalleryItem | undefined = $state();
	let galleryItems = $derived(
		(images || []).map((image) => ({
			media: {
				url: getImageUrl(image?.image?.filename_disk, 'original'),
				width: image?.image?.width || 0,
				height: image?.image?.height || 0,
			},
			title: `${image?.caption}`,
			type: GalleryMedia.image,
		}))
	);

	$effect(() => {
		selectedItem = galleryItems[selectedItemIndex];
	});

	const handleCloseLightbox = () => {
		isLightboxOpen = false;
	};

	const handleSelectNextItem = () => {
		selectedItemIndex = (selectedItemIndex + 1) % galleryItems.length;
	};

	const handleSelectPreviousItem = () => {
		selectedItemIndex =
			+(selectedItemIndex - 1 + galleryItems.length) % galleryItems.length;
	};

	const handleDotClick = (index: number) => {
		selectedItemIndex = index;
	};
</script>

<div data-cy={dataCyPrefix}>
	{#if youtubeUrl}
		<VideoPlayer
			controls
			src={youtubeUrl}
			{title}
			dataCy={`${dataCyPrefix}-video`}
			class="mb-4 pb-[56.25%]"
			navigating={!!navigating.complete}
		/>
	{/if}
	<Txt component="h1" variant="h4" class="mb-3" dataCy={`${dataCyPrefix}-title`}
		>{title}</Txt
	>
	{#if speakers}
		<div class="mb-5" data-cy={`${dataCyPrefix}-speakers`}>
			{#each speakers as speaker, i}
				<Txt
					href={speaker.href}
					component="a"
					variant="label2"
					class="underline">{speaker.name}</Txt
				>{#if i < speakers.length - 1}
					<Txt component="span" variant="label2">{', '}</Txt>
				{/if}
			{/each}
		</div>
	{/if}
	<div class="xs:flex xs:gap-x-2 mb-8 sm:gap-x-4">
		{#if type === 'talk'}
			<LinkButton
				dataCy={dataCyPrefix}
				size="sm"
				class="xs:mb-0 xs:w-fit mb-3 flex w-full items-center justify-center px-0.5"
				href={youtubeUrl}
				newTab
				fullWidth
			>
				<div class="flex items-center">
					<YouTubeIcon class="mr-1 hidden lg:block" color="gray-0" />Watch on
					Youtube
					<YouTubeIcon class="ml-1 lg:hidden" color="gray-0" />
				</div>
			</LinkButton>
		{/if}
		{#if children}
			<div class="xs:hidden block">
				{@render children()}
			</div>
		{/if}
		<div class="flex items-center">
			<LinkButton
				dataCy={dataCyPrefix}
				variant="secondary"
				size="sm"
				class="xs:w-fit m-0 flex w-[calc(100%-34px)] items-center justify-center"
				newTab
				fullWidth
				href={TALK_YOUTUBE_CHANNEL}>Subscribe to HENI Talks</LinkButton
			>
			<Button
				dataCy={dataCyPrefix}
				size="sm"
				variant="secondary"
				class="ml-2 p-0.5 sm:ml-4"
				onclick={handleOpenShareDialog}
			>
				<ShareIcon class="h-5 w-5" />
			</Button>
			{#if children}
				<div class="xs:block hidden">
					{@render children()}
				</div>
			{/if}
		</div>
	</div>
	<Tabs
		bind:activeTab
		dataCy={dataCyPrefix}
		class="mb-8"
		classes={{
			container: '[&_p]:whitespace-break-spaces',
			header: 'w-[calc(100vw-1rem)] xs:w-auto',
		}}
		{tabs}
	>
		{#if tabs[activeTab]?.markdown}
			<Markdown
				dataCy={`${dataCyPrefix}-tabs`}
				source={tabs[activeTab].markdown ?? []}
			/>
		{/if}
	</Tabs>
	{#if tabs[activeTab].id === '5'}
		<div
			class="xs:grid-cols-2 -mt-2 mb-6 grid grid-cols-1 gap-4 md:grid-cols-3"
		>
			{#each images as image, i}
				<img
					class="hidden"
					alt={image?.caption}
					src={getImageUrl(image?.image?.filename_disk, 'original')}
				/>
				<div>
					<button
						class="relative w-full bg-gray-100 pb-[100%]"
						onclick={() => {
							selectedItemIndex = i;
							isLightboxOpen = true;
						}}
					>
						<div
							class="absolute top-6 right-6 bottom-6 left-6 flex items-center justify-center"
						>
							<img
								class="max-h-full"
								alt={image?.caption}
								src={getImageUrl(image?.image?.filename_disk)}
							/>
						</div>
					</button>
					<Txt variant="body3" class="mt-2 text-gray-500">
						{image?.caption}
					</Txt>
				</div>
			{/each}

			{#if isLightboxOpen}
				<Lightbox
					dataCy="drop-gallery"
					onClose={handleCloseLightbox}
					{selectedItem}
					{selectedItemIndex}
					itemsLength={(images || []).length}
					onClickLeftArrow={handleSelectPreviousItem}
					onClickRightArrow={handleSelectNextItem}
					onDotClick={handleDotClick}
				/>
			{/if}
		</div>
	{/if}
	{#if timePeriod}
		<div class="mb-8 flex items-center space-x-4">
			<Txt variant="label2">Time Period:</Txt>

			<Link
				data-cy={`${dataCyPrefix}-time-period`}
				class="rounded-lg border border-gray-500 px-2.5 py-1.5"
				href={`${TalksRoutes.Gallery}?4=${encodeURIComponent(
					timePeriod
				)}&4_type=3`}
			>
				<Txt variant="strong2" class="whitespace-nowrap">
					{timePeriod}
				</Txt>
			</Link>
		</div>
	{/if}
	{#if tags?.length}
		<div class="mb-12 flex items-start gap-4">
			<Txt variant="label2" class="mt-[0.4375rem]">Themes:</Txt>

			<div
				class="flex max-w-[calc(100%-5.125rem)] flex-wrap items-center gap-2"
			>
				{#each tags as tag}
					<Link
						data-cy={`${dataCyPrefix}-theme-${tag?.HENI_Talks_Tags_id?.name}`}
						class="inline-block rounded-lg bg-gray-100 px-[0.6875rem] py-[0.4375rem]"
						href={`${TalksRoutes.Gallery}?3=${encodeURIComponent(
							tag?.HENI_Talks_Tags_id?.name as string
						)}&3_type=3`}
					>
						<Txt variant="strong2" class="whitespace-nowrap">
							{tag?.HENI_Talks_Tags_id?.name}
						</Txt>
					</Link>
				{/each}
			</div>
		</div>
	{/if}
</div>

<ShareDialog
	link={shareUrl}
	intro={shareIntro}
	dataCy={dataCyPrefix}
	dialogStores={shareDialogStores}
	showText={false}
/>
